import { useEffect, useRef, useState } from 'react';
import { AddCircleOutline, CancelOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Card, CardHeader, Chip, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { NotifyTypo } from 'web-components';
import { getEquipmentGroupNames, loadIrisEquipmentGroups, updateIrisEquipmentGroups } from 'api/irisEquipmentGroups';
import { ERROR, SUCCESS } from 'attrs/status';
import { generateId } from 'helpers/utils';
import { addNotification } from 'redux/ui/notifications/actions';
import EditEquipmentGroupModal from './EditEquipmentGroupModal';

const labelAmend = 'machines.machine_details.equipment_group';

interface EquipmentGroupProps {
  machineId: string;
}

const EquipmentGroup = ({ machineId }: EquipmentGroupProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [groups, setGroups] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const requestController = useRef<AbortController | null>(null);
  const saving = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    requestController.current = controller;
    setIsLoading(true);
    setLoadError(false);
    setModalOpen(false);
    setGroups([]);
    setIsSaving(false);
    saving.current = false;

    loadIrisEquipmentGroups(machineId, controller.signal)
      .then(({ data }) => {
        if (!controller.signal.aborted) setGroups(getEquipmentGroupNames(data));
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoadError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [machineId, reload]);

  const saveGroups = async (nextGroups: string[], successMessage: string) => {
    const controller = requestController.current;
    if (!controller || controller.signal.aborted || saving.current) return;
    saving.current = true;
    setIsSaving(true);

    try {
      const { data } = await updateIrisEquipmentGroups(machineId, nextGroups, controller.signal);
      if (controller.signal.aborted) return;
      setGroups(getEquipmentGroupNames(data));
      setModalOpen(false);
      dispatch(
        addNotification({
          key: generateId(),
          type: SUCCESS,
          message: `${labelAmend}.${successMessage}`,
          parameters: {}
        })
      );
    } catch {
      if (!controller.signal.aborted) {
        dispatch(
          addNotification({
            key: generateId(),
            type: ERROR,
            message: 'machines.generic_error',
            parameters: {}
          })
        );
      }
    } finally {
      if (!controller.signal.aborted) {
        saving.current = false;
        setIsSaving(false);
      }
    }
  };

  return (
    <Card sx={{ p: 4, my: 2 }} aria-busy={isLoading || isSaving}>
      <CardHeader
        sx={{ p: 0, pt: '15px' }}
        title={t(`${labelAmend}.title`)}
        titleTypographyProps={{ fontWeight: 500, variant: 'body1' }}
      />
      <NotifyTypo.Subtitle1 fontWeight="400">{t(`${labelAmend}.subtitle`)}</NotifyTypo.Subtitle1>
      <Box sx={{ mt: 3, mb: 1 }}>
        <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.info_table.equipment_group`)}</NotifyTypo.InfoText>
        {isLoading ? (
          <CircularProgress size={20} sx={{ mt: 1 }} />
        ) : loadError ? (
          <Alert
            severity="error"
            action={
              <Button data-test-id="equipment-group-retry" color="inherit" onClick={() => setReload(reload + 1)}>
                {t('errors.try_reload')}
              </Button>
            }
          >
            {t('errors.generic')}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mt: 0.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
              {groups.length ? (
                groups.map(group => (
                  <Chip
                    key={group}
                    label={group}
                    color="primary"
                    size="small"
                    disabled={isSaving}
                    data-test-id={`equipment-group-${encodeURIComponent(group)}`}
                    onDelete={() =>
                      void saveGroups(
                        groups.filter(name => name !== group),
                        'success_removed'
                      )
                    }
                    deleteIcon={
                      <CancelOutlined
                        data-test-id={`equipment-group-remove-${encodeURIComponent(group)}`}
                        aria-hidden="true"
                      />
                    }
                    aria-label={`${t('form.delete')} ${group}`}
                    sx={{ height: 24, maxWidth: '100%', '& .MuiChip-deleteIcon': { fontSize: 16 } }}
                  />
                ))
              ) : (
                <NotifyTypo.Body1>--</NotifyTypo.Body1>
              )}
            </Box>
            <Button
              variant="outlined"
              size="small"
              endIcon={<AddCircleOutline />}
              data-test-id="equipment-group-add"
              disabled={isSaving}
              onClick={() => setModalOpen(true)}
              sx={{ borderRadius: 4, height: 24, textTransform: 'none', flexShrink: 0 }}
            >
              {t('form.add')}
            </Button>
          </Box>
        )}
      </Box>
      {modalOpen && (
        <EditEquipmentGroupModal
          groups={groups}
          isSaving={isSaving}
          onClose={() => setModalOpen(false)}
          onSave={nextGroups => saveGroups(nextGroups, 'success_added')}
        />
      )}
    </Card>
  );
};

export default EquipmentGroup;
